import { Flex, Progress, Text } from "@chakra-ui/react"
import { Button, Heading } from "DesignSystem/components"
import { OnboardingStep } from "UserCrowd/hooks/useActionSteps"
import { ROUTES } from "UserCrowd/views/routes"
import React from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import { useActionsDrawerContext } from "./ActionsDrawerContextProvider"
import { OnboardingCard, OnboardingCardProps } from "./OnboardingCard"
import { OnboardingStatus, OnboardingStepKey } from "./constants"

const STATIC_ONBOARDING_STEPS: OnboardingStepKey[] = [
  "agreement",
  "basic_details",
  "phone_number_verification",
  "notification_preferences",
]

const STEP_CONTENT: Record<
  OnboardingStepKey,
  { title: string; description?: string; actions?: React.ReactNode }
> = {
  agreement: {
    title: "Accept the Tester Agreement",
  },
  basic_details: {
    title: "Add your basic details",
  },
  phone_number_verification: {
    title: "Verify your phone number",
  },
  notification_preferences: {
    title: "Set your notification preferences",
  },
  practice_test: {
    title: "Complete the practice test",
    description:
      "This must be completed in order to unlock real, paid opportunities. Your response will be reviewed by the UserCrowd team.",
    actions: (
      <Flex gap={4} alignItems="center">
        <Button
          size="compact"
          variant="primary"
          as={ReactRouterLink}
          to={ROUTES.DASHBOARD.START_PRACTICE_TEST.path}
        >
          Take now
        </Button>
        <Text textStyle="ds.paragraph.secondary" color="ds.text.subtle">
          Approx 1 minute
        </Text>
      </Flex>
    ),
  },
}

export const OnboardingPanel: React.FC = () => {
  const { onboardingSteps: dynamicOnboardingSteps, handleOnboardingDismiss } =
    useActionsDrawerContext()

  const applicableSteps = buildOnboardingSteps(dynamicOnboardingSteps)
  const stepsCount = applicableSteps.length

  const stepsCompletedCount = applicableSteps.filter(
    (step: { status: OnboardingStatus }) => step.status === "complete"
  ).length

  return (
    <>
      <Flex flexDirection="column" gap={6} px={3} w="full">
        <Heading as="h2" textStyle="ds.heading.emphasized">
          Complete one more step to start earning!
        </Heading>
        <Flex gap={3} w="full">
          <Progress
            flexGrow={1}
            value={stepsCompletedCount}
            min={0}
            max={stepsCount}
            bg="ds.background.neutral.resting"
            rounded="full"
            alignSelf="stretch"
            // Chakra provides no prop to override the track color
            sx={{
              "> *": {
                backgroundColor: "ds.background.brand.bold.resting !important",
              },
            }}
          />
          <Text textStyle="ds.interface.medium">
            {stepsCompletedCount}/{stepsCount}
          </Text>
        </Flex>
      </Flex>

      <Flex
        flexDirection="column"
        gap={3}
        w="full"
        rounded="12px"
        bg="ds.background.neutral.resting"
        p={3}
      >
        {applicableSteps.map((step, index) => (
          <OnboardingCard key={index} {...step} />
        ))}
        {stepsCompletedCount === stepsCount && (
          <Button variant="primary" onClick={handleOnboardingDismiss} mt={3}>
            All done!
          </Button>
        )}
      </Flex>
    </>
  )
}

function buildOnboardingSteps(
  dynamicOnboardingSteps: OnboardingStep[]
): OnboardingCardProps[] {
  return [
    ...STATIC_ONBOARDING_STEPS.map((key) => {
      const content = STEP_CONTENT[key]
      if (!content) return null

      return {
        title: content.title,
        description: content.description,
        actions: content.actions,
        status: "complete",
        required: false,
      }
    }),
    ...dynamicOnboardingSteps
      .filter((step) => step.status !== "not_required")
      .map((step) => {
        const content = STEP_CONTENT[step.key]
        if (!content) return null

        return {
          title: content.title,
          description: content.description,
          actions: content.actions,
          status: step.status,
          required: true,
        }
      }),
  ].filter((step): step is OnboardingCardProps => step !== null)
}
